import React, { useEffect, useState, useCallback } from "react";

import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Tooltip,
  Grid,
  CircularProgress,
  Link as MUILink,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ViewInArIcon from "@mui/icons-material/ViewInAr";

import { SolutionsFrame } from "components/solutions/SolutionsFrame";
import { BlockStandard } from "components/admin/BlockStandard";
import { Offer, Offers } from "api/onboardingTypes";
import { useGreenEnergyContext } from "contexts/GreenEnergyContext";
import { SpaceSelector } from "components/admin/SpaceSelector";
import { fetchCityOffers } from "api/onboarding";
import { Link } from "react-router-dom";
import { useUserContext } from "contexts/UserContext";

const CONTRACT_FORM = "contract-form";

const INVOICE_UPLOAD_PAGE = "/onboarding/consumer-bill-upload";

const TOOLTIP_TEXT =
  "É o limite de desconto que pode ser aplicado em cada kWh de energia compensada, comparado ao valor cobrado pela distribuidora. Ex.: se a tarifa da distribuidora é R$ 1,00/kWh e o desconto máximo definido é 20%, o preço do kWh compensado será R$ 0,80.";

export const GreenEnergyOffers = ({ location, history }) => {
  const { currentSpace, token } = useUserContext();
  const { setOfferId, building } = useGreenEnergyContext();
  const [offers, setOffers] = useState<Offers>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadOffers = useCallback(async () => {
    try {
      const offers = await fetchCityOffers(
        token,
        building?.city || currentSpace?.properties[0]?.city || ""
      );
      setOffers(offers);
      setIsLoading(false);
    } catch (error) {
      history.push(INVOICE_UPLOAD_PAGE);
    }
  }, [history, token, building, currentSpace]);

  useEffect(() => {
    if (!offers.length) {
      if (location.state?.offers) {
        setOffers(location.state.offers);
        setIsLoading(false);
      } else {
        loadOffers();
      }
    }
  }, [offers, location, loadOffers]);

  const handleButton = (offer: Offer) => {
    setOfferId(offer.contractId);
    history.push(CONTRACT_FORM);
  };

  return (
    <SolutionsFrame
      title="Contrato de energia verde"
      currentStep={2}
      totalSteps={6}
    >
      {!location?.state?.offers && (
        <BlockStandard hideDivider>
          <SpaceSelector />
        </BlockStandard>
      )}

      {isLoading && (
        <Grid item container justifyContent="center">
          <CircularProgress size={90} />
        </Grid>
      )}

      {offers.map((offer) => (
        <BlockStandard hideDivider key={offer.name}>
          <Card sx={{ p: 2 }}>
            <CardMedia
              image={offer.image}
              sx={{ height: "150px", borderRadius: "8px" }}
            />
            <CardContent>
              <Typography variant="h2" sx={{ mb: 2 }}>
                {offer.name}
              </Typography>
              <Grid container item alignItems="center" gap={1}>
                <img
                  src="/static/icons/coin-hand.svg"
                  alt="Ilustração de um painel solar"
                  style={{ height: "24px" }}
                />
                <Typography variant="h3" component="span">
                  até
                </Typography>
                <Typography variant="h2" component="span">
                  {`${offer.discount}%`}
                </Typography>
                <Typography variant="h3" component="span">
                  de desconto
                </Typography>
                <Tooltip title={TOOLTIP_TEXT} sx={{ marginLeft: "auto" }}>
                  <HelpOutlineIcon style={{ opacity: 0.5 }} />
                </Tooltip>
              </Grid>

              {offer.aboutThePowerPlant.tour && (
                <MUILink
                  href={offer.aboutThePowerPlant.tour}
                  style={{ textDecoration: "none" }}
                >
                  <Grid
                    container
                    item
                    alignItems="center"
                    gap={1}
                    sx={{ my: 2 }}
                  >
                    <ViewInArIcon color="secondary" />
                    <Typography
                      variant="h3"
                      component="span"
                      fontWeight="normal"
                    >
                      Tour virtual
                    </Typography>
                  </Grid>
                </MUILink>
              )}

              <Link
                to={{ pathname: "offer-detail", state: { offer } }}
                style={{ textDecoration: "none" }}
              >
                <Typography
                  variant="h3"
                  component="span"
                  fontWeight="normal"
                  sx={{ textDecoration: "underline" }}
                >
                  Ver detalhamento da oferta
                </Typography>
              </Link>
            </CardContent>
            <CardActionArea>
              <Button
                fullWidth
                variant="contained"
                onClick={() => handleButton(offer)}
              >
                Contratar
              </Button>
            </CardActionArea>
          </Card>
        </BlockStandard>
      ))}
    </SolutionsFrame>
  );
};
